import { useEffect, useState } from 'react'

// Router
import { useLocation, useNavigate, useParams } from 'react-router-dom'

// Services
import { enqueueSnackbar } from 'notistack'
import {
  useGetSurgeryQuery,
  useSetSurgeryStatusMutation,
  useGetProceduresQuery,
} from 'lib/apollo/hooks'

// States
import { useAuthActions } from 'store/auth/auth.store'
import { useAuth0 } from 'app/Auth'
import {
  useDateRange,
  useFilterPeriod,
  useNotificationActions,
  useNotificationPopupOpen,
  useNotifications,
  useSurgeryActions,
  useSurgeryStatus,
} from 'store/notifications'
import { getUserRoleString, useUser } from 'app/User'
import { useOrganizationSettings } from 'lib/context/OrganizationSettingsContext'
import { useDashboardContext } from 'lib/context/DashboardContext'

// Types
import { Surgery } from 'common/types'

// Utils
import { useIsRouteActive } from 'lib/utils/isRouteActive'
import { loadNotifications } from 'lib/utils/loadNotifications'
import { ANALYTICS, getTitleMap, ROOM_VIEW } from 'app/ROUTES_CONSTANTS'
import { useFlags } from 'launchdarkly-react-client-sdk'
import dayjs from 'lib/dayjs'

export const useFloatingBarLogic = () => {
  const flags = useFlags()
  const navigate = useNavigate()
  const location = useLocation()
  const organizationSettings = useOrganizationSettings()
  const { showScrubTechnician } = useOrganizationSettings()
  const { surgeryId } = useParams<{ surgeryId: Surgery['_id'] | undefined }>()

  const [setSurgeryStatus] = useSetSurgeryStatusMutation(surgeryId || '')

  const { data: surgeryData, loading: isLoadingNotifications } =
    useGetSurgeryQuery(surgeryId, {
      disablePolling: true,
    })

  const isEditingSubmittedSurgery = surgeryId
    ? surgeryData?.getSurgery?.status?.isEditingSubmittedSurgery || false
    : false

  const { isActive } = useIsRouteActive()
  const { user, isNurseAdmin, isGuest, isRep } = useUser()
  const { setDarkMode, darkMode } = useDashboardContext()

  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false)

  const showSettings = {
    [ROOM_VIEW]: location.pathname.includes(ROOM_VIEW) && isNurseAdmin,
  }

  const { logout } = useAuth0()

  const { handleLogout: handleLogoutAction } = useAuthActions()
  const dateRange = useDateRange()
  const filterPeriod = useFilterPeriod()
  const notifications = useNotifications()
  const isNotificationPopupOpen = useNotificationPopupOpen()
  const surgeryStatus = useSurgeryStatus()
  const { openSurgeryReminderModal } = useSurgeryActions()
  const {
    setFilterPeriod,
    setNotifications,
    toggleNotificationPopup,
    closeNotificationPopup,
  } = useNotificationActions()

  const { data: proceduresData, loading: isLoadingProcedures } =
    useGetProceduresQuery({
      endDate: dateRange.before,
      startDate: dateRange.after,
      pendingOnly: true,
      skip: isRep,
    })

  const userRoleString = user && getUserRoleString(user)
  const isSPD = user?.roles?.includes('MGAT_SPD')

  const getDynamicTitle = (location: ReturnType<typeof useLocation>) => {
    const titleMap = getTitleMap(organizationSettings)
    const rootPath = location.pathname.slice(1) as keyof typeof titleMap
    return titleMap[rootPath] ?? 'Platform'
  }

  const handleMenuToggle = () => {
    closeNotificationPopup()
    setIsSettingsPopupOpen(false)
    setIsMenuOpen(!isMenuOpen)
  }

  const handleLogout = async () => {
    if (isRep || notifications.length === 0) {
      handleLogoutAction(() => {
        logout({ returnTo: `${window.location.origin}/?status=loggedout` })
      })
      return
    }

    const confirmed = await openSurgeryReminderModal('logout')
    if (!confirmed) return

    handleLogoutAction(() => {
      logout({ returnTo: `${window.location.origin}/?status=loggedout` })
    })
    setIsMenuOpen(false)
  }

  const handleNavigate = (route: string) => {
    setIsMenuOpen(false)
    navigate(`/${route}`)
  }

  const handleLogoBtnClick = async () => {
    if (isGuest) return
    if (isRep) {
      handleNavigate(isSPD ? 'tray-management' : 'surgeries')
      return
    }

    if (surgeryId) {
      if (surgeryStatus === 'SCANNING') {
        const confirmed = await openSurgeryReminderModal('insideCase')
        if (!confirmed) return
      }
    }

    handleNavigate(isSPD ? 'tray-management' : 'surgeries')
  }

  const handleThemeMode = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDarkMode(e.target.checked)
  }

  const handleExitAddendumMode = () => {
    if (!surgeryId) return
    setSurgeryStatus({
      variables: {
        surgeryId,
        status: 'SUBMITTED',
        isEditingSubmittedSurgery: false,
        rep: `${user?.firstName} ${user?.lastName}`,
        dateTime: dayjs().toISOString(),
      },
    })
      .then(() => {
        navigate(`/surgeries/${surgeryId}`)
      })
      .catch((error) => {
        enqueueSnackbar('Error exiting addendum mode', {
          variant: 'error',
        })
      })
  }

  const handleNotificationToggle = () => {
    toggleNotificationPopup()
    if (isNotificationPopupOpen) {
      const notifications = loadNotifications(proceduresData)
      setNotifications(notifications)
    }
  }

  const handleSettingsToggle = () => {
    if (isNotificationPopupOpen) {
      closeNotificationPopup()
    }
    setIsSettingsPopupOpen(!isSettingsPopupOpen)
  }

  const handleFilterChange = (period: 'today' | '7days') => {
    setFilterPeriod(period)
  }

  const shouldHideDepartmentSwitcher = () => {
    if (surgeryId) return true
    if (location.pathname === '/new-procedure') return true

    return false
  }

  useEffect(() => {
    if (proceduresData?.getProcedures?.procedures) {
      const notifications = loadNotifications(proceduresData)
      setNotifications(notifications)
    }
  }, [proceduresData, setNotifications])

  useEffect(() => {
    setIsMenuOpen(false)
    closeNotificationPopup()
    setIsSettingsPopupOpen(false)
  }, [location, closeNotificationPopup])

  return {
    user,
    isSPD,
    flags,
    darkMode,
    location,
    isMenuOpen,
    isNotificationPopupOpen,
    isSettingsPopupOpen,
    notifications,
    isLoadingNotifications,
    dynamicTitle: getDynamicTitle(location),
    isNurseAdmin,
    userRoleString,
    isDashboardActive: isActive(ANALYTICS),
    PhysiciansTitle: organizationSettings.physiciansTitle,
    isEditingSubmittedSurgery,
    isGuest,
    showScrubTechnician,
    shouldHideDepartmentSwitcher: shouldHideDepartmentSwitcher(),
    handleExitAddendumMode,
    setDarkMode,
    handleLogout,
    handleNavigate,
    getDynamicTitle,
    handleThemeMode,
    handleMenuToggle,
    handleNotificationToggle,
    handleSettingsToggle,
    handleFilterChange,
    filterPeriod,
    handleLogoBtnClick,
    isLoadingProcedures,
    showSettings,
  }
}
